import { Fragment, useMemo, useState } from 'react';
import {
  SlateLabelEnum,
  type ContestSettings,
  type PayoutSchedule,
  ContestSegmentType,
} from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/testing';
import { SegmentedControl } from '~/domains/common/components/SegmentedControl/SegmentedControl';
import { Text } from '~/domains/design-system/Text';

import { SectionGrid, SectionGridCol, SectionNote } from '../components';

type PrizesProps = {
  prizes: PayoutSchedule[];
  slateLabel?: SlateLabelEnum;
  segments?: ContestSettings['segments'];
};

const enum SegmentTab {
  OVERALL = 'overall',
  SEGMENT = 'segment',
}

function PrizesTable({ prizes = [], slateLabel, segments }: PrizesProps) {
  const { t } = useTranslation('contest');
  const [activeTab, setActiveTab] = useState<SegmentTab>(SegmentTab.OVERALL);
  const sortedPrizes = useMemo(() => [...prizes].sort((a, b) => a.order - b.order), [prizes]);
  const sortedSegmentPrizes = useMemo(
    () => (segments ? [...segments.prizes].sort((a, b) => a.order - b.order) : []),
    [segments]
  );
  const isSegmentedPayout = segments?.segmentType === ContestSegmentType.MULTI_SLATE;
  const isSlatePayout = segments?.segmentType === ContestSegmentType.SINGLE_SLATE;

  const segmentLabel = useMemo(() => {
    if (isSegmentedPayout) return t('detail.prizes.tabs.segments');
    if (isSlatePayout) {
      if (slateLabel === SlateLabelEnum.WEEK) return t('detail.prizes.tabs.slatePayoutWeek');
      if (slateLabel === SlateLabelEnum.TOURNAMENT)
        return t('detail.prizes.tabs.slatePayoutTournament');
      if (slateLabel === SlateLabelEnum.SLATE) return t('detail.prizes.tabs.slatePayoutSlate');
      return t('detail.prizes.tabs.slatePayoutSlate');
    }
    return null;
  }, [t, isSegmentedPayout, isSlatePayout, slateLabel]);

  const tabs = [
    { label: t('detail.prizes.tabs.overall'), value: SegmentTab.OVERALL },
    { label: segmentLabel, value: SegmentTab.SEGMENT },
  ];

  const items = activeTab === SegmentTab.OVERALL ? sortedPrizes : sortedSegmentPrizes;

  return (
    <SectionNote mb={4}>
      {isSegmentedPayout || isSlatePayout ? (
        <SegmentedControl
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as SegmentTab);
          }}
          data={tabs.map(({ label, value }) => ({
            value,
            label,
          }))}
          w="100%"
          mb="lg"
        />
      ) : null}
      {items.map((prize, i) => (
        <Fragment key={prize.value}>
          <SectionGrid data-test-id={dt.contestDetail.section.prizes.row}>
            <SectionGridCol span={9} md={8}>
              <Text variant="body-medium">{prize.label}</Text>
            </SectionGridCol>
            <SectionGridCol span={3} md={4}>
              <Text variant="subhead-medium">{prize.value}</Text>
            </SectionGridCol>
          </SectionGrid>
        </Fragment>
      ))}
    </SectionNote>
  );
}

export default PrizesTable;
